import * as Logger from "js-logger";
import { Signal, SignalBinding } from "signals";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import ControlList from "slotworks/smart-ui/control-list";
import { Control } from "slotworks/smart-ui/controls/control";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import HeadlessSmartUIGroup, { SmartUIGroup } from "slotworks/smart-ui/smart-ui-group";
import Contract from "slotworks/utils/contracts/contract";

export default class AutoSmartUIGroup extends HeadlessSmartUIGroup implements SmartUIGroup {

    public onShow: Signal;
    public onHide: Signal;
    public onDestroy: Signal;
    public autoCacheWhenDisabled: boolean = false;

    protected layer: Layers;
    protected scene: string;
    protected attachedButtons: FlexiButton[] = [];

    private isActive: boolean;
    private destroySignal: SignalBinding;

    constructor(name: string, layer: Layers, scene: string) {
        super(name);

        this.layer = layer;
        this.scene = scene;

        this.onShow = new Signal();
        this.onHide = new Signal();
        this.onDestroy = new Signal();

        if (this.layer.hasScene(scene)) {
            this.layer.onSceneEnter.add((sceneName: string) => {
                if (sceneName === this.scene) {
                    this.setup(this.scene);
                }
            });

            if (this.layer.currentSceneIs(this.scene)) {
                this.setup(this.scene);
            } else {
                this.visible = false;
            }
        } else {
            Logger.error("The layer " + layer.container.name + " does not have a scene called " + scene + ", so a group cannot be created");
        }
    }

    public show(): Contract<any> {
        if (!this.visible) {
            this.onShow.dispatch(name);
        }

        super.show().execute();
        this.layer.show();

        if (this.isActive && this.layer.currentSceneIs(this.scene)) {
            return Contract.empty();
        } else {
            return this.layer.setScene(this.scene);
        }
    }

    public hide(): void {
        if (this.visible) {
            this.onHide.dispatch(name);
        }
        if (this.layer.currentSceneIs(this.scene)) {
            if (this.autoCacheWhenDisabled) {
                this.layer.cacheAsBitmap(false);
            }

            this.layer.defaultScene().execute();
            this.layer.hide();
        }

        super.hide();
    }

    public setEnabled(enabled: boolean): void {
        if (this.autoCacheWhenDisabled && this.layer.currentSceneIs(this.scene)) {
            this.layer.cacheAsBitmap(!enabled);
        }
        super.setEnabled(enabled);
    }

    public setSelected(selected: boolean): void {
        this.attachedButtons.forEach((button: FlexiButton) => button.setSelected(selected));
    }

    public attachButton(button: FlexiButton): void {
        if (this.attachedButtons.indexOf(button) === -1) {
            this.attachedButtons.push(button);
        }
    }

    private setup(sceneName: string): void {
        this.isActive = true;
        if (sceneName === this.scene) {
            this.destroySignal = this.layer.onSceneExit.add(() => this.destroy());
            this.scanForControls();
            this.setFlags(true);
            this.setEnabled(true);
            this.setSelected(true);
        } else {
            this.destroy();
        }
    }

    private scanForControls(): void {
        let control: Control;
        const controls = ControlList.getAllControls();

        for (const controlList of controls) {

            if (controlList.layerHasThisControl(this.layer)) {
                control = controlList.getNewType();
                control.setup(controlList.getName(), this.layer, controlList.assetName, controlList.getAllAssetNames(), this);

                this.controls.push(control);
            }
        }
    }

    private setFlags(show: boolean): void {
        this.flagsOnShow.forEach((flagOnShow) => {
            SmartUIFlags.set(flagOnShow, show);
        });
    }

    private detachDestorySignalBinding(): void {
        if (this.destroySignal) {
            this.destroySignal.detach();
            this.destroySignal = null;
        }
    }

    private destroyControls(): void {
        for (const control of this.controls) {
            control.destroy();
        }
        this.controls = [];
    }

    private destroy(): void {
        this.onDestroy.dispatch();
        this.isActive = false;
        this.setSelected(false);
        this.detachDestorySignalBinding();
        this.destroyControls();
        this.setFlags(false);
    }
}
